/**
 * Subtle device-orientation tilt for mobile. Reads gamma (left-right tilt)
 * and beta (front-back tilt), low-pass-smooths them, and writes a clamped
 * angle into the dedicated `.card-gyro` layer via two CSS variables
 * (--gyro-x, --gyro-y). On iOS 13+ Safari, DeviceOrientationEvent permission
 * must be requested from a user gesture; we arm a one-shot listener.
 */
const MAX_TILT_X_DEG = 7;
const MAX_TILT_Y_DEG = 12;
const SMOOTHING = 0.08;
const BETA_NEUTRAL = 35;

interface RequestableDeviceOrientation {
  requestPermission?: () => Promise<"granted" | "denied" | "prompt">;
}

export class GyroController {
  private readonly layer: HTMLElement;
  private targetX = 0;
  private targetY = 0;
  private currentX = 0;
  private currentY = 0;
  private rafId = 0;
  private listening = false;

  constructor(layer: HTMLElement) {
    this.layer = layer;
    if (typeof window === "undefined") return;
    if (!("DeviceOrientationEvent" in window)) return;

    const ctor = window.DeviceOrientationEvent as unknown as RequestableDeviceOrientation;
    if (typeof ctor.requestPermission === "function") {
      this.armPermissionRequest(ctor);
    } else {
      this.attach();
    }
  }

  private armPermissionRequest(ctor: RequestableDeviceOrientation): void {
    const handler = async (): Promise<void> => {
      window.removeEventListener("pointerdown", handler);
      window.removeEventListener("touchstart", handler);
      try {
        const result = await ctor.requestPermission?.();
        if (result === "granted") this.attach();
      } catch {
        /* user denied or unsupported — silently no-op */
      }
    };
    window.addEventListener("pointerdown", handler, { once: true, passive: true });
    window.addEventListener("touchstart", handler, { once: true, passive: true });
  }

  private attach(): void {
    if (this.listening) return;
    this.listening = true;
    window.addEventListener("deviceorientation", this.onOrientation, { passive: true });
    this.startSmoothing();
  }

  private onOrientation = (event: DeviceOrientationEvent): void => {
    const gamma = event.gamma ?? 0;
    const beta = event.beta ?? BETA_NEUTRAL;
    this.targetY = clamp((gamma / 45) * MAX_TILT_Y_DEG, -MAX_TILT_Y_DEG, MAX_TILT_Y_DEG);
    this.targetX = clamp(((beta - BETA_NEUTRAL) / 45) * MAX_TILT_X_DEG, -MAX_TILT_X_DEG, MAX_TILT_X_DEG);
  };

  private startSmoothing(): void {
    if (this.rafId) return;
    const tick = (): void => {
      this.currentX += (this.targetX - this.currentX) * SMOOTHING;
      this.currentY += (this.targetY - this.currentY) * SMOOTHING;
      this.layer.style.setProperty("--gyro-x", `${this.currentX.toFixed(2)}deg`);
      this.layer.style.setProperty("--gyro-y", `${this.currentY.toFixed(2)}deg`);
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  dispose(): void {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    if (this.listening) {
      window.removeEventListener("deviceorientation", this.onOrientation);
    }
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
